/**
 * A Promise-based wrapper for the SourceBuffer's `appendBuffer` method.
 * The MediaSource API is event-based. This utility hides that complexity by
 * resolving a promise when the 'updateend' event fires, allowing for
 * cleaner async/await syntax in the streaming loop.
 * @param sourceBuffer The MediaSource SourceBuffer to append to.
 * @param buffer The audio data chunk (BufferSource) to append.
 */
export const appendBufferAsync = (sourceBuffer: SourceBuffer, buffer: BufferSource): Promise<void> => {
	return new Promise<void>((resolve, reject) => {
		try {
			sourceBuffer.addEventListener('updateend', () => resolve(), { once: true });
			sourceBuffer.appendBuffer(buffer);
		} catch (e) {
			reject(e);
		}
	});
};

/**
 * Computes buffered time ahead of the current playhead.
 */
export const getBufferAheadSeconds = (audio: HTMLAudioElement) => {
	try {
		const currentPlaybackTime = audio.currentTime;
		const ranges = audio.buffered;
		for (let i = 0; i < ranges.length; i++) {
			const start = ranges.start(i);
			const end = ranges.end(i);
			if (start <= currentPlaybackTime && currentPlaybackTime <= end) {
				return Math.max(0, end - currentPlaybackTime);
			}
		}
		return 0;
	} catch {
		return 0;
	}
};

/**
 * Waits until bufferAhead drops to or below the resume threshold.
 */
export const waitForBufferToDrain = async (
	audio: HTMLAudioElement,
	isReadyToConsumeStream: boolean,
	resumeThresholdSeconds: number,
): Promise<void> => {
	return new Promise<void>((resolve) => {
		const evaluateResumeCondition = () => {
			if (!isReadyToConsumeStream) {
				cleanup();
				resolve();
				return;
			}
			const bufferAheadSeconds = getBufferAheadSeconds(audio);
			if (bufferAheadSeconds <= resumeThresholdSeconds || audio.ended) {
				cleanup();
				resolve();
			}
		};

		const onTimeUpdate = () => evaluateResumeCondition();
		const onSeeked = () => evaluateResumeCondition();
		audio.addEventListener('timeupdate', onTimeUpdate);
		audio.addEventListener('seeked', onSeeked);
		// run an immediate check in case we're already under the threshold
		evaluateResumeCondition();

		function cleanup() {
			audio.removeEventListener('timeupdate', onTimeUpdate);
			audio.removeEventListener('seeked', onSeeked);
		}
	});
};
